import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { TextArea } from '../ui/TextArea';
import { 
  CreateSupportTicketRequest, 
  SupportCategory, 
  SupportCategoryInfo,
  SupportTicketCreateResponse 
} from '../../types';
import { supportApi } from '../../services/supportApi';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

// Form validation schema
const supportTicketSchema = z.object({
  subject: z.string()
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject must be less than 100 characters'),
  description: z.string()
    .min(10, 'Please provide more details (at least 10 characters)')
    .max(2000, 'Description must be less than 2000 characters'),
  category: z.string().refine(val => {
    if (val === '' || val === undefined) return false;
    const num = Number(val);
    return !isNaN(num) && num >= 0 && num <= 8;
  }, 'Please select a category'),
});

type SupportTicketFormData = z.infer<typeof supportTicketSchema>;

interface SupportContactFormProps {
  onSuccess?: (ticket: SupportTicketCreateResponse) => void;
  onCancel?: () => void;
}

export default function SupportContactForm({ onSuccess, onCancel }: SupportContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<SupportCategoryInfo[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const { user } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<SupportTicketFormData>({
    resolver: zodResolver(supportTicketSchema),
    mode: 'onChange',
  });

  const descriptionLength = watch('description')?.length || 0;

  // Load support categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await supportApi.getSupportCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to load support categories:', error);
        toast.error('Failed to load form data. Please refresh and try again.');
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const onSubmit = async (data: SupportTicketFormData) => {
    if (!user) {
      toast.error('You must be logged in to submit a support ticket');
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData: CreateSupportTicketRequest = {
        subject: data.subject.trim(),
        description: data.description.trim(),
        category: Number(data.category) as SupportCategory,
      };

      const response = await supportApi.createTicket(requestData);

      if (response.success) {
        toast.success('Support ticket submitted successfully! You will receive email updates on your ticket status.');
        reset();
        onSuccess?.(response);
      } else {
        toast.error(response.message || 'Failed to submit support ticket');
      }
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingCategories) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-300">Loading form...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Contact Support
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Describe your issue and we'll get back to you as soon as possible.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* User Info Display */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            Contact Information
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Name:</strong> {user?.firstName} {user?.lastName}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Email:</strong> {user?.email}
          </p>
        </div>

        {/* Category Selection */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Issue Category
          </label>
          <select
            id="category"
            {...register('category')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select a category...</option>
            {categories.map((category, index) => (
              <option key={category.value} value={index}>
                {category.displayName}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.category.message}
            </p>
          )}
          
          {/* Category Description */}
          {watch('category') && watch('category') !== '' && categories[Number(watch('category'))] && (
            <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                {categories[Number(watch('category'))]?.description}
              </p>
            </div>
          )}
        </div>

        {/* Subject */}
        <div>
          <Input
            label="Subject"
            {...register('subject')}
            placeholder="Brief description of your issue"
            error={errors.subject?.message}
            leftIcon={<AlertCircle className="w-4 h-4" />}
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <TextArea
            id="description"
            {...register('description')}
            rows={6}
            placeholder="Please provide as much detail as possible about your issue. Include any error messages, steps to reproduce, or relevant context."
            className="w-full"
          />
          <div className="flex justify-between items-center mt-1">
            {errors.description ? (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.description.message}
              </p>
            ) : (
              <div />
            )}
            <span className={`text-sm ${
              descriptionLength > 1800 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {descriptionLength}/2000
            </span>
          </div>
        </div>

        {/* System Info Notice */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                System Information
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                We'll automatically include your browser and system information to help us diagnose technical issues.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            leftIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </div>
      </form>
    </div>
  );
}
